import { Link } from "wouter";
import { Heart } from "lucide-react";
import type { EventCard as EventCardType } from "../lib/api/useEventsList";
import { useMarkInterested, useUnmarkInterested } from "../lib/api/useEventRsvp";
import { useEventState, useEventStateContext } from "../lib/eventState";
import { useInterestedCelebration } from "../lib/interestedCelebration";
import { useAuth } from "../lib/platform";
import { useOnboardingGate } from "./OnboardingContext";
import { formatRelativeEventDate } from "../utils/dateUtils";
import outcloseIcon from "@/assets/icons/outclose.svg";
import outcloseLogo from "@/assets/icons/outclose_horizontal.svg";
import { EventPoster } from "./EventPoster";
import { getEventType } from "@shared/eventTypes";
import tracking from "../lib/tracking";

export function EventCard({
  event,
  largeCards = true,
}: {
  event: EventCardType;
  index?: number;
  largeCards?: boolean;
}) {
  const cityLine = [event.venue.name, event.venue.neighborhood, event.venue.city].filter(Boolean).join(" - ");
  const relativeDate = event.date ? formatRelativeEventDate(event.date, event.time) : null;
  const displayTags = event.types
    .map((slug) => getEventType(slug))
    .filter((t): t is NonNullable<typeof t> => t !== null)
    .filter((t, _, arr) => !(t.slug === "queer" && arr.length > 2))
    .slice(0, 3);

  const titleClass = largeCards
    ? "text-base sm:text-lg font-semibold leading-snug"
    : "text-sm sm:text-base font-semibold leading-snug";
  const subtitleClass = largeCards
    ? "mt-0.5 text-sm text-neutral-500 dark:text-neutral-400 truncate"
    : "mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 truncate";

  const override = useEventState(event.slug);
  const { setInterested } = useEventStateContext();
  const { openSheet } = useInterestedCelebration();
  const isInterested = override?.isInterested ?? event.isInterested ?? false;
  const { isSignedIn, openSignIn } = useAuth();
  const onboarding = useOnboardingGate();
  // On the events index a signed-out action opens the onboarding intro (and
  // returns the user to this event after signup); elsewhere fall back to the
  // plain sign-in dialog.
  const gateSignIn = () => (onboarding ? onboarding.openOnboarding(event.link) : openSignIn());
  const markInterested = useMarkInterested(event.slug ?? "");
  const unmarkInterested = useUnmarkInterested(event.slug ?? "");
  const interestPending = markInterested.isPending || unmarkInterested.isPending;
  const interestedCount = (event.interestedCount ?? 0)
    + (isInterested && !event.isInterested ? 1 : 0)
    - (!isInterested && event.isInterested ? 1 : 0);

  // Outclose-curated events lead into the auth-gated booking flow — when the
  // user is signed out, open the signup/onboarding dialog instead of navigating.
  const handleCardClick = (e: React.MouseEvent) => {
    tracking.eventsCardClicked(event.slug ?? event.link, { isOutclose: event.isOutclose });
    if (event.isOutclose && !isSignedIn) {
      e.preventDefault();
      gateSignIn();
    }
  };

  const handleHeartClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!event.slug || event.isPast) return;
    if (!isSignedIn) { gateSignIn(); return; }
    tracking.eventInterestedToggled(event.slug, !isInterested);
    if (isInterested) {
      setInterested(event.slug, false);
      unmarkInterested.mutate(undefined, { onError: () => setInterested(event.slug!, true) });
    } else {
      setInterested(event.slug, true);
      openSheet(event.slug, true);
      markInterested.mutate(undefined, { onError: () => setInterested(event.slug!, false) });
    }
  };

  return (
    <div className="relative">
      <Link href={event.link} onClick={handleCardClick} className="block group">
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-neutral-900">
          {event.coverImageUrl ? (
            <img
              src={event.coverImageUrl}
              alt={event.name ?? "Event"}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            />
          ) : (
            <EventPoster name={event.name ?? "Untitled event"} label={event.posterLabel} types={event.types} />
          )}
          {event.isOutclose && (
            <div
              className={`absolute bottom-0 inset-x-0 flex items-center justify-center gap-1.5 backdrop-blur-md border-t border-white/10 ${largeCards ? "py-3 gap-2" : "py-2 gap-1.5"}`}
              style={{ background: "rgba(76, 29, 149, 0.72)" }}
            >
              <img src={outcloseIcon} alt="" aria-hidden className={`w-auto brightness-0 invert ${largeCards ? "h-5" : "h-3.5"}`} />
              <img src={outcloseLogo} alt="Outclose" className={`w-auto brightness-0 invert ${largeCards ? "h-5" : "h-3.5"}`} />
            </div>
          )}
          {/* Tags overlay */}
          {displayTags.length > 0 && (
            <div className={`absolute inset-x-0 flex flex-wrap justify-center gap-1 px-2 ${
              event.isOutclose
                ? largeCards ? "bottom-[38px]" : "bottom-[24px]"
                : "bottom-1"
            }`}>
              {displayTags.map((tag) => (
                <span
                  key={tag.slug}
                  className="inline-flex items-center gap-0.5 rounded-full text-white font-semibold"
                  style={{
                    backgroundColor: "rgba(12, 8, 28, 0.65)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontSize: largeCards ? "0.68rem" : "0.6rem",
                    padding: largeCards ? "3px 8px" : "2px 5px",
                  }}
                >
                  <span>{tag.emoji}</span>
                  <span className="ml-0.5">{tag.label}</span>
                </span>
              ))}
            </div>
          )}

          {/* Top-right: status badge */}
          {event.isPast ? (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] uppercase tracking-wider">
              Past
            </div>
          ) : event.isRecurring ? (
            <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] uppercase tracking-wider">
              ↻ weekly
            </div>
          ) : null}
        </div>

        <div className="mt-3">
          <h2 className={`${titleClass} line-clamp-2 group-hover:text-primary-500 transition-colors`}>
            {event.name ?? "Untitled event"}
          </h2>
          {/* Relative date/time inline with the venue line */}
          {(relativeDate || cityLine) && (
            <p className={subtitleClass}>
              {relativeDate && <span className="font-medium">{relativeDate}</span>}
              {relativeDate && cityLine && <span> · </span>}
              {cityLine}
            </p>
          )}
        </div>
      </Link>

      {/* Heart button + count — outside Link to avoid navigation on click */}
      {event.slug && !event.isPast && (
        <div className="absolute top-3 left-3">
          <button
            type="button"
            onClick={handleHeartClick}
            disabled={interestPending}
            aria-label={isInterested ? "Remove interest" : "Mark as interested"}
            className={`
              h-8 rounded-full flex items-center gap-1.5 backdrop-blur-md transition-all shadow-sm disabled:opacity-60
              ${interestedCount > 0 ? "px-2.5" : "w-8 justify-center"}
              ${isInterested
                ? "bg-white/90 dark:bg-neutral-900/80 text-rose-500"
                : "bg-black/30 text-white hover:bg-black/50"
              }
            `}
          >
            <Heart className={`w-4 h-4 shrink-0 transition-all ${isInterested ? "fill-current" : ""}`} />
            {interestedCount > 0 && (
              // Signed-out users see that there's interest, but the count itself
              // is blurred until they sign in.
              <span className={`text-xs font-bold leading-none tabular-nums ${isSignedIn ? "" : "blur-[3px] select-none"}`}>
                {interestedCount}
              </span>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
